import { useEffect, useMemo, useState } from "react";
import { fetchLiveArticle, type LiveArticleDto } from "../api";
import { ResearchMatrixTable, type MatrixColumn } from "../components/ResearchMatrixTable";
import { DriftCurveChart, type DriftCurvePoint } from "../components/DriftCurveChart";
import { FieldHelp } from "../components/FieldHelp";
import { PageHelper } from "../components/PageHelper";
import { useSeoTags } from "../useSeoTags";
import "../marketMyths.css";
import "../suePead.css";

const SLUG = "sue-pead";
const CANONICAL = "https://tickerpicker.in/sue-pead";

interface Bucket {
  family: string;
  sector?: string;
  leg: "short" | "pead_60d";
  verdict: "relevant" | "raw_only" | "noise" | "insufficient_history";
  n_confirm_cohorts: number;
  realized_ic_confirm?: number;
  hac_p_raw?: number;
  bh_p_fdr?: number;
  sign_agree?: boolean;
}

interface DriftCurve {
  days: number[];
  diff: (number | null)[];
  n_high: number[];
  n_low: number[];
  n_high_total: number;
  n_low_total: number;
  n_pool_total: number;
}

interface LooDrop {
  cohort: string;
  n: number | null;
  dropped_ic: number | null;
  remaining_mean_ic?: number;
  hac_p_raw_loo?: number;
  bh_p_fdr_reranked?: number;
  flips_relevant_to_raw_only?: boolean;
  skipped: boolean;
}

interface LooBucket {
  sector: string;
  leg: "short" | "pead_60d";
  verdict: string;
  bh_p_fdr?: number;
  n_cohorts_tested: number;
  n_cohorts_skipped: number;
  n_survives: number;
  drops: LooDrop[];
}

interface SuePeadResult {
  as_of: string;
  n_events_eligible: number;
  sectors: string[];
  primary_buckets: Bucket[];
  drift_curves: Record<string, DriftCurve>;
  leave_one_out?: Record<string, LooBucket>;
  limitation_survivorship: string;
}

function sectorLabel(sector: string): string {
  return sector
    .replace(/^sector:/, "")
    .split("-")
    .map((w) => w[0].toUpperCase() + w.slice(1))
    .join(" ");
}

function fmtIc(v: number | null | undefined): string {
  if (v === null || v === undefined) return "—";
  return `${v >= 0 ? "+" : ""}${v.toFixed(4)}`;
}

function fmtP(v: number | null | undefined): string {
  if (v === null || v === undefined) return "—";
  return v < 0.001 ? v.toExponential(1) : v.toFixed(3);
}

function fmtDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString("en-IN", { year: "numeric", month: "short", day: "numeric" });
}

const VERDICT_LABEL: Record<string, string> = {
  relevant: "RELEVANT",
  raw_only: "RAW_ONLY (caught)",
  noise: "noise",
  insufficient_history: "insufficient history",
};

export function SuePeadPage() {
  const [data, setData] = useState<LiveArticleDto | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [chartSector, setChartSector] = useState<string>("sector:consumer-cyclical");

  // Humanized-voice rewrite signed off 2026-10-28 (reviewed on an
  // isolated preview build, flipped together with the rest of the Market
  // Myths batch). Live data fetching, chart wiring, and component usage
  // are unchanged from the original build -- only the hardcoded English
  // copy changed register.

  useEffect(() => {
    fetchLiveArticle(SLUG)
      .then(setData)
      .catch(() => setError("Could not load this article's live data."));
  }, []);

  const result = data?.result as unknown as SuePeadResult | undefined;

  useSeoTags({
    title: "Post-earnings drift on the NSE is real, weak, and won't sit still — Tickerpicker",
    description: "A living, monthly-recomputed test of whether stocks keep drifting in the direction of an " +
      "earnings surprise for weeks afterward. Real sector-by-sector results, an interactive drift curve, and " +
      "the discipline that caught its own false positive.",
    canonical: CANONICAL,
    ogType: "article",
    jsonLd: result
      ? {
          "@context": "https://schema.org",
          "@type": "Article",
          headline: "Post-earnings drift on the NSE is real, weak, and won't sit still",
          description: "A living, monthly-recomputed sector-by-sector PEAD test on NSE equities.",
          url: CANONICAL,
          publisher: { "@type": "Organization", name: "Tickerpicker" },
          isPartOf: { "@type": "WebPage", name: "Market Myths: Tested", url: "https://tickerpicker.in/market-myths" },
        }
      : undefined,
  });

  const matrixColumns: MatrixColumn[] = [
    { key: "sector", label: "Sector" },
    { key: "short_ic", label: "Short IC" },
    { key: "short_p", label: "Short p (FDR)" },
    { key: "short_verdict", label: "Short verdict" },
    { key: "pead_ic", label: "60d IC" },
    { key: "pead_p", label: "60d p (FDR)" },
    { key: "pead_verdict", label: "60d verdict" },
  ];

  const matrixRows = useMemo(() => {
    if (!result) return [];
    return result.sectors.map((sector) => {
      const short = result.primary_buckets.find((b) => b.sector === sector && b.leg === "short");
      const pead = result.primary_buckets.find((b) => b.sector === sector && b.leg === "pead_60d");
      return {
        sector: sectorLabel(sector),
        _sector_key: sector,
        short_ic: short?.realized_ic_confirm ?? null,
        short_p: short?.bh_p_fdr ?? null,
        short_verdict: short ? VERDICT_LABEL[short.verdict] : "—",
        pead_ic: pead?.realized_ic_confirm ?? null,
        pead_p: pead?.bh_p_fdr ?? null,
        pead_verdict: pead ? VERDICT_LABEL[pead.verdict] : "—",
      };
    });
  }, [result]);

  function matrixCellFormat(value: unknown, key: string): string {
    if (key.endsWith("_ic")) return fmtIc(value as number | null);
    if (key.endsWith("_p")) return fmtP(value as number | null);
    if (value === null || value === undefined) return "—";
    return String(value);
  }

  const finServ = result?.primary_buckets.find((b) => b.sector === "sector:financial-services" && b.leg === "short");

  const chartCurve = result?.drift_curves[chartSector];
  const chartPoints: DriftCurvePoint[] = useMemo(() => {
    if (!chartCurve) return [];
    return chartCurve.days.map((day, i) => ({
      day, diff: chartCurve.diff[i], nHigh: chartCurve.n_high[i], nLow: chartCurve.n_low[i],
    }));
  }, [chartCurve]);

  const chartVerdict = result?.primary_buckets.find((b) => b.sector === chartSector && b.leg === "pead_60d");

  // THE CENTERPIECE LESSON, computed live every month, never hardcoded:
  // the LARGEST-magnitude day-60 reading among sectors the rigorous test
  // calls null, vs the SMALLEST-magnitude reading among sectors it calls
  // real. Only rendered when that contrast genuinely holds this month
  // (the null one really is bigger) -- an honest, self-gating design, not
  // a fixed "Comm Services vs Industrials" story that could go stale or
  // become false as the data (and the recompute) moves forward.
  const magnitudeLesson = useMemo(() => {
    if (!result) return null;
    const withDay60 = (verdictFilter: (v: string) => boolean) => {
      const out: { sector: string; day60: number; pFdr?: number }[] = [];
      for (const sector of result.sectors) {
        const curve = result.drift_curves[sector];
        const bucket = result.primary_buckets.find((b) => b.sector === sector && b.leg === "pead_60d");
        if (!curve || !bucket || !verdictFilter(bucket.verdict)) continue;
        const day60 = curve.diff[curve.diff.length - 1];
        if (day60 === null) continue;
        out.push({ sector, day60, pFdr: bucket.bh_p_fdr });
      }
      return out;
    };
    const relevant = withDay60((v) => v === "relevant");
    const nullish = withDay60((v) => v === "noise" || v === "raw_only");
    if (relevant.length === 0 || nullish.length === 0) return null;
    const smallestRelevant = relevant.reduce((a, b) => (Math.abs(a.day60) < Math.abs(b.day60) ? a : b));
    const biggestNull = nullish.reduce((a, b) => (Math.abs(a.day60) > Math.abs(b.day60) ? a : b));
    if (Math.abs(biggestNull.day60) <= Math.abs(smallestRelevant.day60)) return null;
    return { smallestRelevant, biggestNull };
  }, [result]);

  return (
    <div className="mm-shell">
      <nav className="mm-nav">
        <a href="/" className="mm-brand"><span className="dot" />TickerPicker</a>
        <div className="mm-evid-crumbs">
          <a href="/">Home</a>
          <span className="mm-crumb-sep">/</span>
          <a href="/market-myths">Market Myths</a>
        </div>
      </nav>

      <article className="mm-evidence sp-article">
        <div className="mm-kick">Market Myths: Tested — living article</div>
        <h1 className="mm-evidence-title">Post-earnings drift on the NSE is real, weak, and won't sit still</h1>

        <PageHelper>
          This page recomputes itself every month against the real, growing earnings database, so what you're
          reading is current, not a snapshot from whenever we happened to write it. A 60-day drift window takes
          time to fully resolve as new earnings land, which is why monthly, not daily, is the real pace here.
        </PageHelper>

        {error && <div className="mm-error">{error}</div>}
        {!data && !error && <div className="mm-loading">Loading…</div>}

        {data && result && (
          <>
            {data.last_run_failed && (
              <div className="ir-banner ir-banner-error">
                The most recent monthly recompute failed — showing the last successful result instead.
              </div>
            )}
            {data.stale && !data.last_run_failed && (
              <div className="ir-banner ir-banner-stale">
                This result is older than its {data.cadence_days}-day refresh cadence — a recompute is overdue
                but hasn't landed yet. Showing the last successful result.
              </div>
            )}

            <div className="ir-asof-badge">
              <span className="ir-asof-dot" />
              <b>Live — recomputed monthly from real data.</b> As of {fmtDate(data.computed_at)} ·{" "}
              {result.n_events_eligible.toLocaleString()} eligible earnings events · {result.sectors.length} sectors tested
            </div>

            <p className="ir-invite">
              A stock beats expectations and keeps climbing for weeks afterward — that's the classic
              post-earnings-drift story, and the market is supposed to be too efficient for it to survive. We
              tested it anyway, on real NSE earnings history, with a design fixed before we ran a single number:
              one <FieldHelp term="sue_definition" /> <b>standardized surprise (SUE)</b> per announcement, split
              across {result.sectors.length} sectors at two horizons each. At most one or two sectors clear our
              significance bar in any given month — and leave-one-out testing shows even those don't rest on
              robust evidence. The drift is real but faint, and it moves underneath you.
            </p>

            <h2 className="ir-h2">Sector by sector</h2>
            <p className="ir-p">
              Every tested sector, both horizons, sortable — check which ones clear the bar yourself rather
              than take our word for it. <FieldHelp term="discovery_confirm_split" /> Every verdict here
              already passed a discovery/confirm sign-consistency check and a correction for testing every bucket
              in the same family at once, not a bare p-value someone glanced at once.
            </p>

            <ResearchMatrixTable
              rows={matrixRows}
              columns={matrixColumns}
              rowKey={(r) => String(r.sector)}
              cellFormat={matrixCellFormat}
              exportFilename={`sue_pead_sector_results_${result.as_of}.csv`}
              chartSlot={() => (
                <div className="sp-chartblock">
                  <h2 className="ir-h2">Watch the drift accumulate — where it's real</h2>
                  <p className="ir-p">
                    <FieldHelp term="pead_drift_curve" /> This line is descriptive, not the test itself — the
                    average cumulative return of a sector's high-SUE names minus its low-SUE names, day by day
                    after earnings. The real verdict is still the sector's own row in the matrix above.
                  </p>

                  {magnitudeLesson && (
                    <div className="sp-magnitude-lesson">
                      <div className="sp-ml-title">
                        Why the biggest line on this chart isn't the real finding
                      </div>
                      <div className="sp-ml-compare">
                        <div className="sp-ml-item sp-ml-null">
                          <div className="sp-ml-sector">{sectorLabel(magnitudeLesson.biggestNull.sector)}</div>
                          <div className="sp-ml-value">
                            {magnitudeLesson.biggestNull.day60 >= 0 ? "+" : ""}
                            {(magnitudeLesson.biggestNull.day60 * 100).toFixed(1)}%
                          </div>
                          <div className="sp-ml-tag">marked NOISE (p(FDR) = {fmtP(magnitudeLesson.biggestNull.pFdr)})</div>
                        </div>
                        <div className="sp-ml-vs">vs</div>
                        <div className="sp-ml-item sp-ml-real">
                          <div className="sp-ml-sector">{sectorLabel(magnitudeLesson.smallestRelevant.sector)}</div>
                          <div className="sp-ml-value">
                            {magnitudeLesson.smallestRelevant.day60 >= 0 ? "+" : ""}
                            {(magnitudeLesson.smallestRelevant.day60 * 100).toFixed(1)}%
                          </div>
                          <div className="sp-ml-tag">marked RELEVANT (p(FDR) = {fmtP(magnitudeLesson.smallestRelevant.pFdr)})</div>
                        </div>
                      </div>
                      <p className="ir-p">
                        {sectorLabel(magnitudeLesson.biggestNull.sector)}'s pooled day-60 reading is actually{" "}
                        <b>bigger</b> than {sectorLabel(magnitudeLesson.smallestRelevant.sector)}'s — and the
                        test still calls the first one noise and the second real. That's not a contradiction;
                        it's the whole point. A big number from one pooled snapshot is exactly what a small,
                        noisy sample produces some of the time by chance. What makes{" "}
                        {sectorLabel(magnitudeLesson.smallestRelevant.sector)} real isn't the size of its line —
                        it's that the effect holds across many separate reporting-season cohorts, keeps the same
                        sign in a held-out window, and survives correction for testing every bucket on this page
                        at once. We said robustness across cohorts is what makes an effect real — so we ran that
                        test on our own surviving sector: drop any single reporting quarter and it no longer
                        clears correction. It passes today only because all thirteen cohorts line up at once —
                        the very fragility this page warns about, turned on ourselves. See the leave-one-out
                        chart below. The bigger line from {sectorLabel(magnitudeLesson.biggestNull.sector)} has
                        none of the above behind it either way. Size and significance are different questions,
                        which is exactly why every curve here sits next to a test result instead of asking you
                        to trust whichever line looks biggest.
                      </p>
                    </div>
                  )}

                  {result.leave_one_out && Object.keys(result.leave_one_out).length > 0 && (
                    <div className="sp-loo-section">
                      <h2 className="ir-h2">Leave-one-out robustness</h2>
                      <p className="ir-p">
                        For every sector at or near our significance line, we drop one reporting-quarter cohort
                        at a time, re-run the full correction with that cohort removed, and check whether the
                        result still clears it. Computed live every recompute, from the same cohorts in the
                        matrix above — never hand-typed.
                      </p>
                      {Object.values(result.leave_one_out).map((loo) => {
                        const testedDrops = loo.drops.filter((d) => !d.skipped);
                        const maxP = Math.max(0.1, ...testedDrops.map((d) => d.bh_p_fdr_reranked ?? 0));
                        return (
                          <div key={`${loo.sector}|${loo.leg}`} className="sp-loo-block">
                            <div className="sp-loo-title">
                              {sectorLabel(loo.sector)} ({loo.leg === "pead_60d" ? "60-day" : "short-horizon"}) —
                              survives <b>{loo.n_survives} of {loo.n_cohorts_tested}</b> single-cohort drops
                            </div>
                            <div className="sp-loo-chart">
                              <div className="sp-loo-zeroline" style={{ bottom: `${(0.05 / maxP) * 100}%` }}>
                                <span>p = 0.05</span>
                              </div>
                              <div className="sp-loo-bars">
                                {testedDrops.map((d) => (
                                  <div
                                    key={d.cohort}
                                    className="sp-loo-bar-wrap"
                                    title={`${d.cohort}: p(FDR) = ${fmtP(d.bh_p_fdr_reranked)}`}
                                  >
                                    <div
                                      className={
                                        "sp-loo-bar " +
                                        (d.flips_relevant_to_raw_only ? "sp-loo-bar-flip" : "sp-loo-bar-survive")
                                      }
                                      style={{ height: `${((d.bh_p_fdr_reranked ?? 0) / maxP) * 100}%` }}
                                    />
                                    <div className="sp-loo-bar-label">{d.cohort.slice(2, 7)}</div>
                                  </div>
                                ))}
                              </div>
                            </div>
                            <div className="sp-loo-legend">
                              <span className="sp-loo-legend-item">
                                <span className="sp-loo-swatch sp-loo-swatch-survive" /> still relevant after
                                dropping this cohort
                              </span>
                              <span className="sp-loo-legend-item">
                                <span className="sp-loo-swatch sp-loo-swatch-flip" /> flips to raw_only
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  <p className="ir-p">
                    Flip through all {result.sectors.length} sectors and watch how differently the relevant
                    one(s) behave from the rest — but read the verdict above the chart first, every time, not the
                    shape of the line.
                  </p>

                  <div className="sp-gross-warning">
                    ⚠ This curve is GROSS (pre-cost). A real 60-day hold has round-trip trading costs — spread,
                    slippage, market impact — that this line does not net out. A positive-looking drift here is
                    not an automatic tradeable edge.
                  </div>

                  <label className="rmt-label" style={{ marginBottom: 10 }}>
                    Sector
                    <select value={chartSector} onChange={(e) => setChartSector(e.target.value)}>
                      {result.sectors.map((s) => (
                        <option key={s} value={s}>
                          {sectorLabel(s)}
                        </option>
                      ))}
                    </select>
                  </label>

                  {chartVerdict && (
                    <div className={`sp-chart-verdict sp-v-${chartVerdict.verdict}`}>
                      60-day test verdict for {sectorLabel(chartSector)}: <b>{VERDICT_LABEL[chartVerdict.verdict]}</b>
                      {chartVerdict.verdict !== "insufficient_history" && (
                        <> (confirm-window IC {fmtIc(chartVerdict.realized_ic_confirm)}, p(FDR) = {fmtP(chartVerdict.bh_p_fdr)})</>
                      )}
                    </div>
                  )}

                  {chartCurve ? (
                    <DriftCurveChart points={chartPoints} nHighTotal={chartCurve.n_high_total} nLowTotal={chartCurve.n_low_total} />
                  ) : (
                    <div className="empty-state">Not enough resolved events in this sector for a curve.</div>
                  )}
                </div>
              )}
            />

            {finServ && (
              <div className="sp-callout">
                <div className="sp-callout-title">Catching our own false positive</div>
                <p className="ir-p">
                  Financial Services' short-horizon leg looked promising at first glance — a raw p-value of{" "}
                  <b>{fmtP(finServ.hac_p_raw)}</b>, comfortably under the 0.05 line most informal analysis would
                  stop at. Correct across all {matrixRows.length * 2} buckets tested and it lands at p(FDR) ={" "}
                  <b>{fmtP(finServ.bh_p_fdr)}</b> — nowhere close. It's marked <b>RAW_ONLY</b> in the matrix
                  above, not relevant: exactly the shape of false positive this correction exists to catch, and
                  why a bare "p &lt; 0.05" is never enough on its own.
                </p>
              </div>
            )}

            <h2 className="ir-h2">The caveats</h2>
            <ul className="ir-storylist">
              <li><b>Narrow.</b> At most one or two of {result.sectors.length} testable sectors clear our significance bar in any given month — this is not a universal PEAD finding on the NSE.</li>
              <li><b>Fragile to a single quarter.</b> Leave-one-out testing drops the surviving sector below significance for most individual cohorts — a signal at the edge of detectability, not a settled fact.</li>
              <li><b>60-day only, never the short horizon.</b> Every sector's SHORT-horizon leg — the timeframe a trader could actually act on — comes back noise. The drift accumulates slowly; it isn't exploitable on a near-term entry.</li>
              <li><b>Gross, not net of costs.</b> Neither the matrix's IC nor the drift curve accounts for spread, slippage, or market impact over a real 60-day hold — this is a finding about drift existing, not a measured, tradeable edge.</li>
              <li><b>Concentrated in thinner-history names.</b> A separate depth-tier cut (not shown here) found the same pattern strongest in the smaller/younger end of the universe, where fewer analysts are watching — the textbook PEAD mechanism.</li>
              <li><b>Survivorship.</b> {result.limitation_survivorship}</li>
              <li><b>Small-n sectors are noisier.</b> Sectors with small quintiles produce visibly wigglier lines for the same reason a small poll has a wider margin of error — don't read a wiggle in a thin sector as a stronger signal than one in a deep sector.</li>
            </ul>

            <p className="ir-p">
              This reuses the exact pre-registered method (SUE definition, entry timing, cohort/HAC/BH-FDR
              discipline) from the original investigation — see{" "}
              <a href="/market-myths?evidence=sue-pead">the full methodology</a> for the complete writeup, or{" "}
              <a href="/working-paper">the Working Paper</a> for how this fits the project's broader discipline.
            </p>
          </>
        )}

        <div className="mm-footnote">
          This page recomputes automatically on a monthly cadence against the live database — the numbers above
          are never a frozen one-off snapshot. TickerPicker is a personal, non-commercial research project.
          Nothing here is investment advice.
        </div>
      </article>
    </div>
  );
}
